import {
  FaceSmileIcon,
  PhoneIcon,
  PhoneArrowUpRightIcon,
  ArrowsRightLeftIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import {
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter,
  type ActiveFilter
} from '@oysterjs/ui/search-filter';
import {
  CallDirection,
  CallDisconnectionReason,
  CallSentiment,
  type CallFilterInput,
  GetVoiceAgentsDocument,
  type GetVoiceAgentsQuery,
  type GetVoiceAgentsQueryVariables
} from '~/api/graphql';
import { client } from '~/api/client';
import { useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import { BaseDateFilter, getSinceDate } from '~/api/data/filter-base';
import React from 'react';

export const CALLS_FILTER_STORAGE_KEY = 'cara_calls_default_view';

export function useCallFilters() {
  const filterDefinitions = React.useMemo(() => createCallFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters } =
    useFiltersFromLocalStorage<CallFilter>(CALLS_FILTER_STORAGE_KEY, filterDefinitions);
  const currentFilter = React.useMemo(() => {
    const filter = activeFiltersToCallFilter(activeFilters);
    return { direction: CallDirection.Inbound, ...filter };
  }, [activeFilters]);
  return { filterDefinitions, activeFilters, setActiveFilters, initialFilters, currentFilter };
}

export type CallFilter =
  | 'sentiment'
  | 'date'
  | 'phone'
  | 'voiceAgent'
  | 'transferred'
  | 'transferTo';

const SENTIMENT_LABELS: Record<CallSentiment, string> = {
  [CallSentiment.Positive]: 'Positive',
  [CallSentiment.Neutral]: 'Neutral',
  [CallSentiment.Negative]: 'Negative'
};

export function activeFiltersToCallFilter(
  filters: ActiveFilter<CallFilter>[]
): CallFilterInput | undefined {
  if (filters.length === 0) return undefined;
  const out: CallFilterInput = {};
  for (const f of filters) {
    switch (f.type) {
      case 'sentiment':
        out.sentiment = f.value as CallSentiment;
        break;
      case 'phone':
        out.fromNumber = f.value as string;
        break;
      case 'date': {
        const since = getSinceDate(f.value as string);
        if (since) {
          out.startedAt = { ...out.startedAt, after: since };
        }
        break;
      }
      case 'voiceAgent':
        out.voiceAgentId = f.value as string;
        break;
      case 'transferred':
        out.disconnectionReason = CallDisconnectionReason.CallTransfer;
        break;
      case 'transferTo':
        out.transferredToNumber = f.value as string;
        break;
      default:
        break;
    }
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

export class SentimentFilter implements FilterDefinition<CallFilter> {
  readonly type = 'sentiment' as const;
  readonly icon = FaceSmileIcon;
  readonly priority = 0;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<CallFilter>): FilterSuggestion[] {
    return Object.values(CallSentiment)
      .filter(
        (s) =>
          SENTIMENT_LABELS[s].toLowerCase().includes(query.toLowerCase()) ||
          'sentiment'.includes(query.toLowerCase())
      )
      .map((s) => ({
        value: s,
        displayLabel: 'Sentiment is',
        displayValue: SENTIMENT_LABELS[s]
      }));
  }

  deserialize(s: SerializedFilter<CallFilter>): ActiveFilter<CallFilter> | null {
    if (!Object.values(CallSentiment).includes(s.value as CallSentiment)) {
      return null;
    }
    return {
      id: `sentiment-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Sentiment is',
      displayValue: s.displayValue
    };
  }
}

export class PhoneFilter implements FilterDefinition<CallFilter> {
  readonly type = 'phone' as const;
  readonly icon = PhoneIcon;
  readonly priority = 20;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<CallFilter>): FilterSuggestion[] {
    const q = query.trim();
    if (!q || !/[\d()+\- ]/.test(q)) return [];
    return [
      {
        value: q,
        displayLabel: 'Phone contains',
        displayValue: q
      }
    ];
  }

  deserialize(s: SerializedFilter<CallFilter>): ActiveFilter<CallFilter> {
    return {
      id: `phone-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Phone contains',
      displayValue: s.displayValue
    };
  }
}

export class VoiceAgentFilter implements FilterDefinition<CallFilter> {
  readonly type = 'voiceAgent' as const;
  readonly icon = UserCircleIcon;
  readonly priority = 5;
  readonly isUnique = true;
  private cachedAgents: Array<{ id: string; name: string }> | null = null;

  async generate({ query }: FilterGeneratorContext<CallFilter>): Promise<FilterSuggestion[]> {
    if (this.cachedAgents === null) {
      try {
        const data = await client.query<GetVoiceAgentsQuery, GetVoiceAgentsQueryVariables>({
          query: GetVoiceAgentsDocument,
          variables: {}
        });
        this.cachedAgents = (data?.data?.voiceAgents ?? []).map((a) => ({
          id: a.id,
          name: a.name
        }));
      } catch {
        this.cachedAgents = [];
      }
    }

    const q = query.trim().toLowerCase();
    return this.cachedAgents
      .filter((a) => a.name.toLowerCase().includes(q) || 'agent'.includes(q) || 'voice'.includes(q))
      .map((a) => ({
        value: a.id,
        displayLabel: 'Voice agent is',
        displayValue: a.name
      }));
  }

  deserialize(s: SerializedFilter<CallFilter>): ActiveFilter<CallFilter> {
    return {
      id: `voiceAgent-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Voice agent is',
      displayValue: s.displayValue
    };
  }
}

export class TransferredFilter implements FilterDefinition<CallFilter> {
  readonly type = 'transferred' as const;
  readonly icon = ArrowsRightLeftIcon;
  readonly priority = 10;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<CallFilter>): FilterSuggestion[] {
    const q = query.toLowerCase();
    if ('transferred'.includes(q) || 'transfer'.includes(q)) {
      return [
        {
          value: 'true',
          displayLabel: 'Transferred',
          displayValue: 'Yes'
        }
      ];
    }
    return [];
  }

  deserialize(s: SerializedFilter<CallFilter>): ActiveFilter<CallFilter> {
    return {
      id: 'transferred',
      type: this.type,
      value: s.value,
      displayLabel: 'Transferred',
      displayValue: s.displayValue
    };
  }
}

export class TransferToFilter implements FilterDefinition<CallFilter> {
  readonly type = 'transferTo' as const;
  readonly icon = PhoneArrowUpRightIcon;
  readonly priority = 15;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<CallFilter>): FilterSuggestion[] {
    const q = query.trim();
    if (!q || !/[\d()+\- ]/.test(q)) return [];
    return [
      {
        value: q,
        displayLabel: 'Transferred to',
        displayValue: q
      }
    ];
  }

  deserialize(s: SerializedFilter<CallFilter>): ActiveFilter<CallFilter> {
    return {
      id: `transferTo-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Transferred to',
      displayValue: s.displayValue
    };
  }
}

export class CallDateFilter extends BaseDateFilter<CallFilter> {
  constructor() {
    super('date');
  }
}

export const createCallFilterDefinitions = (): FilterDefinition<CallFilter>[] => [
  new SentimentFilter(),
  new VoiceAgentFilter(),
  new TransferredFilter(),
  new TransferToFilter(),
  new PhoneFilter(),
  new CallDateFilter()
];
